import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useFetch from "utils/hooks/useFetch";
import { GET_ALL_TICKETS } from "api";
import { ticket_api } from "api";
import ArrowRight from "pages/assets/icons/arrow-right";
import SearchIcon from "assets/icons/SearchIcon.png";
import FilterIcon from "assets/icons/FilterIcon.png";
import SortbyIcon from "assets/icons/SortbyIcon.png";
import ClickedFilterIcon from "assets/icons/ClickedFilterIcon.png";
import ClickedSortbyIcon from "assets/icons/ClickedSortbyIcon.png";
import Method from "assets/icons/Method.png";
import InvestmentFeeIcon from "assets/icons/InvestmentFeeIcon.png";

// helpers
import { formSortQuery } from "../helpers/format-queries";
import formatCurrency from "../helpers/formatCurrency";
import useToastMessage from "helpers/toast-message";

function WebinarList() {
  const location = useLocation();

  const { showError } = useToastMessage();
  const { fetch: getAllTickets } = useFetch(GET_ALL_TICKETS, false, ticket_api);
  const { t } = useTranslation();

  let autocompleteTimer;

  const [onClickedFilterBtn, setOnClickedFilterBtn] = useState(false);
  const [onClickedSortbyBtn, setOnClickedSortbyBtn] = useState(false);

  const [webinarTickets, setWebinarTickets] = useState([]);

  const [filterValue, setFilterValue] = useState({
    sortById: "DESC",
    sortByWebinarTicketTitle: null,
    type: null,
    search: "",
  });

  const handleFilterBtn = (e, key1, key2) => {
    let finalFilter = filterValue;
    let checked = e.target.checked;
    if (key1 === "sortById") {
      finalFilter = {
        ...finalFilter,
        sortById: key2,
        sortByWebinarTicketTitle: null,
      };
    } else if (key1 === "sortByWebinarTicketTitle") {
      finalFilter = {
        ...finalFilter,
        sortByWebinarTicketTitle: key2,
        sortById: null,
      };
    } else {
      finalFilter = {
        ...finalFilter,
        type: {
          ...finalFilter.type,
          [`${key2 === "hybrid (online and offline)" ? "hybrid" : key2}`]:
            checked,
        },
      };
    }
    setFilterValue(finalFilter);
  };

  const handleOnChangeSearchVal = (e) => {
    if (autocompleteTimer) {
      clearTimeout(autocompleteTimer);
    }
    let val = e.target.value;
    autocompleteTimer = setTimeout(
      () => setFilterValue({ ...filterValue, search: val }),
      1000,
    );
  };

  const fetchAllTickets = (queries) => {
    getAllTickets({ query: queries })
      .then((data) => {
        setWebinarTickets(data);
      })
      .catch((err) => {
        showError(err?.data?.message);
      });
  };

  useEffect(() => {
    let objSortQuery = {
      column: filterValue.sortById
        ? "id"
        : filterValue.sortByWebinarTicketTitle
          ? "title"
          : "",
      state: filterValue.sortById || filterValue.sortByWebinarTicketTitle || "",
    };
    let queries = {
      search: filterValue.search,
      sort: formSortQuery(objSortQuery),
      type: filterValue.type,
      webinar_type:
        location && location.pathname && location.pathname.includes("/webinars")
          ? "Webinar"
          : "Consultation",
    };
    fetchAllTickets(queries);
  }, [filterValue]);

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
        {/* Search Bar */}
        <div className="w-full bg-gray-100 sm:w-auto rounded-2xl">
          <div className="flex items-center p-3">
            <img src={SearchIcon} alt="Search" className="w-6 h-6 mr-3" />
            <input
              onChange={(e) => handleOnChangeSearchVal(e)}
              placeholder="Search program..."
              type="text"
              className="w-full bg-gray-100 border-transparent focus:border-transparent focus:ring-0 focus:outline-none"
            />
          </div>
        </div>

        {/* Filter & Sort Buttons */}
        <div className="relative flex flex-col w-full gap-2 mb-4 sm:w-auto">
          <div className="flex flex-wrap justify-center gap-3 sm:justify-end">
            {/* Filter Button */}
            <button
              className={`rounded-2xl ${
                onClickedFilterBtn ? "bg-app-orange" : "bg-gray-100"
              }`}
              onClick={() => {
                setOnClickedFilterBtn(!onClickedFilterBtn);
                setOnClickedSortbyBtn(false);
              }}
            >
              <div className="flex items-center gap-2 px-6 py-2">
                <img
                  src={onClickedFilterBtn ? ClickedFilterIcon : FilterIcon}
                  alt="Filter"
                  className="w-6 h-6"
                />
                <span className={onClickedFilterBtn ? "text-white" : ""}>
                  Filter
                </span>
              </div>
            </button>

            {/* Sort By Button */}
            <button
              className={`rounded-2xl ${
                onClickedSortbyBtn ? "bg-app-orange" : "bg-gray-100"
              }`}
              onClick={() => {
                setOnClickedSortbyBtn(!onClickedSortbyBtn);
                setOnClickedFilterBtn(false);
              }}
            >
              <div className="flex items-center gap-2 px-6 py-2">
                <img
                  src={onClickedSortbyBtn ? ClickedSortbyIcon : SortbyIcon}
                  alt="Sort by"
                  className="w-6 h-6"
                />
                <span className={onClickedSortbyBtn ? "text-white" : ""}>
                  Sort by
                </span>
              </div>
            </button>
          </div>

          {/* Filter Dropdown */}
          {onClickedFilterBtn && (
            <div className="absolute z-10 w-full p-4 mt-2 shadow-lg top-full bg-app-orange rounded-2xl sm:w-64">
              <div className="mb-2 font-bold text-white">
                By Method of Learning
              </div>
              <div className="my-2 border-t border-b border-white" />
              <div className="flex flex-col gap-2">
                {["Online", "Offline", "Hybrid (Online and Offline)"].map(
                  (label) => (
                    <label key={label} className="flex items-center text-white">
                      <input
                        type="checkbox"
                        name={label}
                        checked={filterValue?.type?.[label]}
                        onChange={(e) =>
                          handleFilterBtn(e, "type", label.toLowerCase())
                        }
                        className="mr-2 border-transparent outline-none bg-app-orange"
                      />
                      {label}
                    </label>
                  ),
                )}
              </div>
            </div>
          )}

          {/* Sort Dropdown */}
          {onClickedSortbyBtn && (
            <div className="absolute right-0 z-10 w-full p-4 mt-2 shadow-lg top-full bg-app-orange rounded-2xl sm:w-64">
              {[
                { label: "A - Z", value: ["sortByWebinarTicketTitle", "ASC"] },
                { label: "Z - A", value: ["sortByWebinarTicketTitle", "DESC"] },
                { label: "Newest Added", value: ["sortById", "DESC"] },
                { label: "Oldest", value: ["sortById", "ASC"] },
              ].map(({ label, value }) => (
                <button
                  key={label}
                  className="w-full py-1 text-left text-white"
                  onClick={(e) => handleFilterBtn(e, value[0], value[1])}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {webinarTickets &&
      Array.isArray(webinarTickets) &&
      webinarTickets.length > 0 ? (
        <div className="grid grid-cols-1 gap-8 pt-2 pb-6 md:grid-cols-3 md:gap-12 md:pt-4 md:pb-12">
          {webinarTickets.map((ticket) => {
            return (
              <div
                key={ticket && ticket.id}
                className="flex gap-4 px-5 py-3 mb-3 border-2 shadow-md cursor-pointer md:flex-col md:gap-6 md:px-5 md:py-5 md:mb-5 rounded-xl border-slate-100"
              >
                <div className="overflow-hidden bg-black h-36 w-60 md:w-full md:h-48 rounded-xl">
                  <img
                    src={ticket && ticket.thumbnail_url}
                    className="object-cover w-full h-full"
                  />
                </div>
                <div className="flex flex-col justify-between w-full h-full space-y-1">
                  <div className="">
                    <div className="font-bold capitalize text-md sm:text-2xl text-app-orange max-h-[100px]">
                      {ticket && ticket.title}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <img
                      src={InvestmentFeeIcon}
                      alt="academy"
                      style={{ width: "30px", height: "auto" }}
                    />
                    <div className="text-sm font-semibold sm:text-md">
                      {formatCurrency(ticket && ticket.price)} / {t("session")}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 pt-3">
                    <img
                      src={Method}
                      alt="academy"
                      style={{ width: "25px", height: "auto" }}
                    />
                    <div className="text-sm font-semibold capitalize sm:text-md">
                      {ticket && ticket.type}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <a
                      href={`/${location && location.pathname && location.pathname.includes("/webinars") ? "price" : "investment"}-details/${ticket && ticket.id}`}
                    >
                      <div className="flex items-center gap-1 mt-5 text-sm font-bold select-none sm:text-base md:mt-6">
                        {t("viewDetails")}
                        <ArrowRight fill={"#FF7B00"} width={15} />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center w-full gap-5 py-10">
          <img
            src={SearchIcon}
            alt="search icon"
            style={{ width: "100px", height: "auto" }}
          />
          <div className="text-2xl font-semibold">Program not found.</div>
        </div>
      )}
    </div>
  );
}

export default WebinarList;
